import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight } from "lucide-react";

import { api } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import { BreadcrumbJsonLd, OrganizationJsonLd } from "@/components/Schemas";
import WhyCheckExpert from "@/components/WhyCheckExpert";

export default function ChecksHub() {
  const [data, setData] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://checkexpert.be";

  useEffect(() => {
    api.get("/seo-pages/checks").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="py-16 text-center text-ink-soft font-modern">Laden…</div>;

  const breadcrumbs = [{ name: "Alle checks", url: "/checks" }];

  return (
    <div className="bg-background font-modern" data-testid="checks-hub">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.meta_description} />
        <link rel="canonical" href={`${origin}/checks`} />
        <link rel="alternate" hrefLang="nl-BE" href={`${origin}/checks`} />
        <link rel="alternate" hrefLang="nl-NL" href={`${origin}/checks`} />
        <link rel="alternate" hrefLang="x-default" href={`${origin}/checks`} />
      </Helmet>
      <OrganizationJsonLd origin={origin} />
      <BreadcrumbJsonLd items={breadcrumbs} origin={origin} />

      <div className="max-w-7xl mx-auto px-6 pt-10 pb-20">
        <Breadcrumbs items={breadcrumbs} />

        <header className="mt-6 max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7]">Alle checks</span>
          <h1 className="font-editorial text-5xl sm:text-6xl font-medium text-ink mt-3 leading-[1.04]" style={{ letterSpacing: "-0.025em" }}>
            {data.h1}
          </h1>
          <p className="text-ink-soft text-lg mt-5 leading-relaxed">{data.intro}</p>
          <p className="text-xs text-ink-subtle mt-4">Laatst bijgewerkt: {data.last_updated}</p>
        </header>

        <div className="mt-12 space-y-12">
          {data.categories.map((cat) => (
            <section key={cat.slug} data-testid={`cat-${cat.slug}`}>
              <div className="flex items-baseline gap-4 mb-5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: cat.color }} aria-hidden />
                <h2 className="font-editorial text-2xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
                  {cat.label}
                </h2>
                <span className="text-xs text-ink-subtle">{cat.checks.length} {cat.checks.length === 1 ? "check" : "checks"}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.checks.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/checks/${c.slug}`}
                    className="group rounded-2xl border border-border bg-white p-5 hover:border-[#002FA7]/30 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                    data-testid={`hub-check-${c.slug}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-editorial text-lg font-semibold text-ink" style={{ letterSpacing: "-0.015em" }}>
                        {c.display_name || c.name}
                      </h3>
                      <ArrowUpRight className="h-4 w-4 text-ink-soft group-hover:text-[#002FA7] transition-colors" />
                    </div>
                    <p className="text-sm text-ink-soft mt-2 leading-relaxed line-clamp-2">{c.tagline}</p>
                    {c.theme_slug && (
                      <p className="text-[11px] text-ink-subtle mt-3">
                        Ook bekend als: <span className="text-ink-soft">{c.theme_slug.replace(/-/g, " ")}</span>
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <WhyCheckExpert ctaHref="/checks" />
    </div>
  );
}
